// assets/edm-bundle-api.js
// Nur Kommunikation mit Shopify (Cart Add, Clear, Fetch Recommendations) - Keine UI-Logik!

window.EdumonBundle = window.EdumonBundle || {};

window.EdumonBundle.API = {
  // === CART CACHE (Context7: Single Source of Truth) ===
  // Context7: Cache cart result within a sync cycle to reduce redundant fetchCart calls
  _cartCache: null,
  
  /**
   * Get cached cart or fetch fresh
   * @returns {Promise<Object>} Cart data
   */
  getCartCached: async function() {
    if (!this._cartCache) {
      this._cartCache = await this.fetchCart();
    }
    return this._cartCache;
  },
  
  /**
   * Invalidate cart cache
   */
  invalidateCache: function() {
    this._cartCache = null;
  },
  
  /**
   * Fetch cart from Shopify
   * @returns {Promise<Object>} Cart data
   */
  fetchCart: async function() {
    const res = await fetch(window.EdumonBundle.Config.ROUTES.CART_JS, {
      headers: { 'Accept': 'application/json' }
    });
    return res.json();
  },
  
  /**
   * Cart update with sections support
   * Context7: Return both response and data to enable sections collection
   * 
   * @param {Object} updates - Updates object {lineKey: qty}
   * @returns {Promise<Object>} {res, data} Response and data
   */
  cartUpdateWithSections: async function(updates) {
    const payload = { updates };
    const cartEl = document.querySelector(window.EdumonBundle.Config.SELECTORS.CART_NOTIFICATION) || 
                   document.querySelector(window.EdumonBundle.Config.SELECTORS.CART_DRAWER);
    const RENDER_SECTIONS = (cartEl?.getSectionsToRender?.() || []).map(s => s.section || s.id);
    if (Array.isArray(RENDER_SECTIONS) && RENDER_SECTIONS.length) {
      payload.sections = RENDER_SECTIONS;
      payload.sections_url = window.location.pathname; // wichtig: muss mit "/" starten
    }
    const res = await fetch(window.EdumonBundle.Config.ROUTES.CART_UPDATE_JS, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
        'Accept': 'application/json'
      },
      credentials: 'same-origin',
      body: JSON.stringify(payload)
    });
    const data = await res.json();
    return { res, data }; // Return both response and data
  },
  
  /**
   * Cart add items
   * @param {Array} items - Items array [{id, quantity, properties}]
   * @param {Array} sections - Optional sections to render
   * @returns {Promise<Object>} Response data
   */
  cartAdd: async function(items, sections = null) {
    const payload = { items };
    if (sections && Array.isArray(sections) && sections.length) {
      payload.sections = sections.join(',');
      payload.sections_url = window.location.pathname;
    }
    const res = await fetch((window.Shopify?.routes?.cart_add_url || '/cart/add') + '.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
        'Accept': 'application/json'
      },
      credentials: 'same-origin',
      body: JSON.stringify(payload)
    });
    return res.json();
  },
  
  /**
   * Set line properties
   * @param {string} lineKey - Line item key
   * @param {Object} nextProps - Properties object
   * @returns {Promise<void>}
   */
  setLineProps: async function(lineKey, nextProps) {
    await fetch(window.EdumonBundle.Config.ROUTES.CART_CHANGE_JS, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      credentials: 'same-origin',
      body: JSON.stringify({ id: lineKey, properties: nextProps })
    });
  },
  
  /**
   * Set line quantity and properties
   * @param {string} lineKey - Line item key
   * @param {number} qty - Quantity
   * @param {Object} nextProps - Properties object
   * @returns {Promise<void>}
   */
  setLineQtyAndProps: async function(lineKey, qty, nextProps) {
    await fetch(window.EdumonBundle.Config.ROUTES.CART_CHANGE_JS, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      credentials: 'same-origin',
      body: JSON.stringify({ id: lineKey, quantity: qty, properties: nextProps })
    });
  },
  
  /**
   * Get sections to render from cart element
   * @returns {Array<string>} Section IDs
   */
  getSectionsToRender: function() {
    const cartEl = document.querySelector(window.EdumonBundle.Config.SELECTORS.CART_NOTIFICATION) || 
                   document.querySelector(window.EdumonBundle.Config.SELECTORS.CART_DRAWER);
    return (cartEl?.getSectionsToRender?.() || []).map(s => s.section || s.id);
  },
  
  /**
   * Handle sections rendering
   * Context7: Robust section rendering with DOM verification, state cleanup, and retry logic
   * 
   * @param {Object} data - Sections data
   * @param {string} source - Source identifier
   * @returns {Promise<boolean>} Success status
   */
  handleSections: async function(data, source = 'bb-bundle') {
    // === INPUT VALIDATION (Context7: Fail Fast) ===
    if (!data || typeof data !== 'object') {
      console.error('[Bundle Builder] Invalid data structure', data);
      return false;
    }
    
    // Normalize data format (handle both {sections: {...}} and direct sections)
    const sections = data.sections || data;
    if (!sections || typeof sections !== 'object') {
      console.error('[Bundle Builder] No sections found in data', data);
      return false;
    }
    
    // === GET DRAWER ELEMENT ===
    const drawerEl = document.querySelector(window.EdumonBundle.Config.SELECTORS.CART_NOTIFICATION) || 
                     document.querySelector(window.EdumonBundle.Config.SELECTORS.CART_DRAWER);
    if (!drawerEl) {
      console.error('[Bundle Builder] Drawer element not found');
      return false;
    }
    
    // === STORE INITIAL STATE (Context7: Initial State Check) ===
    // Context7: Detect structure state to handle empty→filled transitions
    const hasEmptyState = !!drawerEl.querySelector('.drawer__inner-empty');
    const hasFilledState = !!drawerEl.querySelector('.drawer__cart-items-wrapper');
    const initialCount = drawerEl.querySelectorAll('tbody tr.cart-item, .cart-item').length;
    
    // === RENDER CONTENTS (Context7: Defensive Programming) ===
    let renderSuccess = false;
    try {
      if (typeof drawerEl.renderContents === 'function') {
        drawerEl.renderContents({ sections });
        renderSuccess = true;
      } else if (sections) {
        // Fallback: harte Section-Replacements
        for (const [sid, html] of Object.entries(sections)) {
          if (!html) continue;
          const el = document.getElementById(`shopify-section-${sid}`);
          if (el) el.outerHTML = html;
        }
        renderSuccess = true;
      }
    } catch (err) {
      console.error('[Bundle Builder] renderContents failed', err);
    }
    
    // === DOM VERIFICATION (Context7: Trust but Verify, Async-Safe) ===
    await new Promise(resolve => {
      requestAnimationFrame(() => {
        setTimeout(resolve, 200); // Initial 200ms delay for structure transitions
      });
    });
    
    // Wait for DOM mutations to complete (max 2s = 40 checks × 50ms)
    const maxAttempts = window.EdumonBundle.Config.CONSTANTS.VERIFICATION_MAX_ATTEMPTS;
    const intervalMs = window.EdumonBundle.Config.CONSTANTS.VERIFICATION_INTERVAL_MS;
    let attempts = 0;
    const renderVerified = await new Promise(resolve => {
      const verifyRenderSuccess = () => {
        const hasWrapper = !!drawerEl.querySelector('.drawer__cart-items-wrapper');
        const hasTbody = !!drawerEl.querySelector('tbody');
        const cartItems = drawerEl.querySelectorAll('tbody tr.cart-item, .cart-item');
        const currentCount = cartItems.length;
        
        const structureTransition = hasWrapper && hasEmptyState;
        const itemsExist = currentCount > 0;
        const countIncreased = currentCount > initialCount;
        const success = structureTransition || itemsExist || countIncreased;
        
        return success;
      };
      
      const checkInterval = setInterval(() => {
        attempts++;
        const success = verifyRenderSuccess();
        if (success) {
          clearInterval(checkInterval);
          resolve(true);
        } else if (attempts >= maxAttempts) {
          clearInterval(checkInterval);
          console.warn('[Bundle Builder] Verification timeout after', attempts, 'attempts');
          resolve(false);
        }
      }, intervalMs);
    });
    
    // === STATE CLEANUP (Context7: Defensive Programming) ===
    if (renderVerified) {
      drawerEl.classList.remove(window.EdumonBundle.Config.CLASSES.EMPTY);
      const cartDrawerItems = drawerEl.querySelector(window.EdumonBundle.Config.SELECTORS.CART_DRAWER_ITEMS);
      if (cartDrawerItems) cartDrawerItems.classList.remove(window.EdumonBundle.Config.CLASSES.EMPTY);
      
      const emptyState = drawerEl.querySelector('.drawer__inner-empty');
      if (emptyState) {
        emptyState.style.display = 'none';
      }
      
      const itemsRoot = document.getElementById('CartDrawer-CartItems');
      const btn = document.getElementById('CartDrawer-Checkout');
      if (itemsRoot && btn) {
        const hasRow = !!(itemsRoot.querySelector('tbody tr.cart-item:not([style*="display: none"])'));
        if (hasRow) {
          btn.disabled = false;
        }
      }
      
      document.dispatchEvent(new CustomEvent('cart:state-changed', { 
        detail: { from: hasEmptyState ? 'empty' : 'filled', to: 'filled', success: true } 
      }));
    }
    
    // === OPEN DRAWER AND DISPATCH EVENT ===
    if (drawerEl && typeof drawerEl.open === 'function') {
      requestAnimationFrame(() => {
        drawerEl.open();
      });
    }
    
    document.dispatchEvent(new CustomEvent('cart:updated', { 
      detail: { source: source, success: renderVerified } 
    }));
    
    return renderVerified;
  },
  
  /**
   * Handle sections with verification and retry logic
   * Context7: Robust section rendering with DOM verification, state cleanup, and retry logic
   * 
   * @param {Object} data - Sections data
   * @param {string} source - Source identifier
   * @param {number} maxRetries - Maximum retry attempts
   * @returns {Promise<boolean>} Success status
   */
  handleSectionsWithVerification: async function(data, source = 'bb-bundle', maxRetries = 2) {
    // === RETRY LOGIC (Context7: Conditional Retry, Avoid Unnecessary Retries) ===
    for (let attempt = 0; attempt <= maxRetries; attempt++) {
      const success = await this.handleSections(data, source);
      if (success) return true;
      
      // Only retry if DOM verification failed AND no items exist in drawer
      const cartEl = document.querySelector(window.EdumonBundle.Config.SELECTORS.CART_NOTIFICATION) || 
                     document.querySelector(window.EdumonBundle.Config.SELECTORS.CART_DRAWER);
      const cartItems = cartEl?.querySelectorAll(window.EdumonBundle.Config.SELECTORS.CART_ITEMS) || [];
      if (cartItems.length > 0) {
        // Items exist, render was successful even if verification timed out
        return true;
      }
      
      // Retry with exponential backoff (500ms, 1000ms)
      if (attempt < maxRetries) {
        const delay = attempt === 0 ? 500 : 1000;
        await window.EdumonBundle.Utils.bbDelay(delay);
        
        // Refresh sections via GET before retry
        try {
          const cartEl = document.querySelector(window.EdumonBundle.Config.SELECTORS.CART_NOTIFICATION) || 
                         document.querySelector(window.EdumonBundle.Config.SELECTORS.CART_DRAWER);
          const RENDER_SECTIONS = this.getSectionsToRender();
          if (Array.isArray(RENDER_SECTIONS) && RENDER_SECTIONS.length) {
            const url = `${window.location.pathname}?sections=${encodeURIComponent(RENDER_SECTIONS.join(','))}`;
            const res2 = await fetch(url, { 
              credentials: 'same-origin', 
              headers: { 'X-Requested-With': 'XMLHttpRequest' } 
            });
            const json2 = await res2.json();
            data = { sections: json2 };
          }
        } catch (err) {
          console.error('[Bundle Builder] Refresh failed before retry', err);
        }
      }
    }
    return false;
  },
  
  /**
   * Read all bundle groups of a product
   * @param {number} productId - Product ID
   * @returns {Promise<Map>} Map of bundleKey -> {lines, totalQty}
   */
  readAllBundleGroupsOfProduct: async function(productId) {
    // Context7: Single Source of Truth - Always fetch fresh cart data
    // Using getCartCached() could return stale data if called after API calls that modify cart
    // This ensures we always have the latest cart state, especially after syncCartBundleToState()
    const cart = await this.fetchCart();
    const map = new Map();
    for (const li of (cart.items || [])) {
      const props = li.properties || {};
      const bkey = props._bundle_key || props.bundle_key || null;
      if (!bkey) continue;
      if (Number(li.product_id) !== productId) continue;
      if (!map.has(bkey)) map.set(bkey, { lines: [], totalQty: 0 });
      const grp = map.get(bkey);
      grp.lines.push({ 
        key: li.key, 
        variantId: li.variant_id, 
        qty: li.quantity, 
        properties: props, 
        productId: li.product_id 
      });
      grp.totalQty += li.quantity;
    }
    return map;
  },
  
  /**
   * Read existing bundle group of this product
   * @param {number} productId - Product ID
   * @param {string} preferredKey - Preferred bundle key
   * @returns {Promise<Object|null>} Bundle group or null
   */
  readExistingBundleGroupOfProduct: async function(productId, preferredKey = null) {
    const groups = await this.readAllBundleGroupsOfProduct(productId);
    if (groups.size === 0) return null;
    const preferred = preferredKey && groups.has(preferredKey)
      ? preferredKey
      : [...groups.entries()].sort((a, b) => b[1].totalQty - a[1].totalQty)[0][0];
    const grp = groups.get(preferred);
    return { 
      bundleKey: preferred, 
      tier: (grp.lines[0]?.properties?._bundle_tier || grp.lines[0]?.properties?.bundle_tier || null), 
      lines: grp.lines, 
      totalQty: grp.totalQty 
    };
  },
  
  /**
   * Read bundle group by key
   * @param {number} productId - Product ID
   * @param {string} bundleKey - Bundle key
   * @returns {Promise<Object|null>} Bundle group or null
   */
  readBundleGroupByKey: async function(productId, bundleKey) {
    const groups = await this.readAllBundleGroupsOfProduct(productId);
    if (!groups.has(bundleKey)) return null;
    const grp = groups.get(bundleKey);
    return { 
      bundleKey, 
      tier: (grp.lines[0]?.properties?._bundle_tier || grp.lines[0]?.properties?.bundle_tier || null), 
      lines: grp.lines, 
      totalQty: grp.totalQty 
    };
  },
  
  /**
   * Remove all lines of this product
   * @param {number} productId - Product ID
   * @returns {Promise<Object>} {data} Response data
   */
  removeAllLinesOfProduct: async function(productId) {
    const cart = await this.getCartCached(); // Context7: Use cached cart to reduce redundant calls
    const updates = {};
    for (const li of (cart.items || [])) {
      if (Number(li.product_id) === productId) updates[li.key] = 0;
    }
    if (Object.keys(updates).length) {
      const { data } = await this.cartUpdateWithSections(updates); // Context7: Use new return format
      return { data }; // Context7: Return sections if available
    }
    return { data: null };
  },
  
  /**
   * Load bundle from cart into state
   * Context7: Single Source of Truth - Loads bundle state from cart and updates state instance
   * Migrated from bb-api-LEGACY.js
   * 
   * @param {number} productId - Product ID
   * @param {Object} state - State instance (from window.EdumonBundle.State.init())
   * @param {string|null} preferredKey - Preferred bundle key (optional)
   * @returns {Promise<Object|null>} Bundle group or null
   */
  loadBundleFromCart: async function(productId, state, preferredKey = null) {
    // Context7: Fail Fast - Validate inputs
    if (!productId || !state) {
      console.error('[Bundle Builder] loadBundleFromCart: Invalid parameters', { productId, state });
      return null;
    }
    
    const cart = await this.fetchCart();
    const map = new Map();
    
    // Group cart items by bundle key
    for (const li of (cart.items || [])) {
      const props = li.properties || {};
      const bkey = props._bundle_key || props.bundle_key;
      if (!bkey || Number(li.product_id) !== productId) continue;

      if (!map.has(bkey)) map.set(bkey, { lines: [], totalQty: 0 });
      const grp = map.get(bkey);
      grp.lines.push({
        key: li.key,
        variantId: li.variant_id,
        qty: li.quantity,
        properties: props,
        productId: li.product_id
      });
      grp.totalQty += li.quantity;
    }

    if (map.size > 0) {
      // Select preferred bundle key or largest bundle
      const selectedKey = (preferredKey && map.has(preferredKey))
        ? preferredKey
        : [...map.entries()].sort((a, b) => b[1].totalQty - a[1].totalQty)[0][0];
      
      const grp = map.get(selectedKey);
      
      // Update state with bundle data
      const items = {};
      for (const ln of grp.lines) {
        const vid = String(ln.variantId);
        items[vid] = (items[vid] || 0) + ln.qty;
      }
      
      // Context7: Resilience - Cap to maximum if needed
      const totalQty = Object.values(items).reduce((a, b) => a + b, 0);
      if (totalQty > state.maxSlots) {
        console.warn('[Bundle Builder] loadBundleFromCart: Total quantity exceeds maximum, capping to', state.maxSlots);
        state.setItems(window.EdumonBundle.Utils.capToMaximum(items, state.maxSlots));
      } else {
        state.setItems(items);
      }
      
      state.setActiveBundleKey(selectedKey);
      state.markClean();
      
      return {
        bundleKey: selectedKey,
        tier: (grp.lines[0]?.properties?._bundle_tier || grp.lines[0]?.properties?.bundle_tier || null),
        lines: grp.lines,
        totalQty: grp.totalQty
      };
    } else {
      // No bundle found - reset state
      state.reset();
      return null;
    }
  },
  
  /**
   * Check if product exists in cart
   * Context7: Utility function for cart state checks
   * Migrated from bb-api-LEGACY.js
   * 
   * @param {number} productId - Product ID
   * @returns {Promise<boolean>} True if product exists in cart
   */
  hasProductInCart: async function(productId) {
    // Context7: Fail Fast - Validate input
    if (!productId) {
      console.error('[Bundle Builder] hasProductInCart: Invalid productId', productId);
      return false;
    }
    
    const cart = await this.fetchCart();
    return (cart.items || []).some(item => Number(item.product_id) === productId);
  },
  
  /**
   * Fetch product by handle
   * @param {string} productHandle - Product handle
   * @returns {Promise<Object>} Product data
   */
  fetchProduct: async function(productHandle) {
    let shopifyRoot = '';
    if (window.Shopify?.routes?.root) {
      shopifyRoot = window.Shopify.routes.root;
    } else if (window.Shopify?.routes) {
      shopifyRoot = window.Shopify.routes.root || '';
    }
    if (shopifyRoot && !shopifyRoot.endsWith('/')) {
      shopifyRoot += '/';
    }
    
    const productUrl = shopifyRoot + window.EdumonBundle.Config.ROUTES.PRODUCTS_JS(productHandle);
    const response = await fetch(productUrl);
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    return response.json();
  }
};

